import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import { toLocaleString } from "@circle-react/helpers/number";
import { MinimalProgressBar } from "@circle-react-uikit/MinimalProgressBar";
import { Typography } from "@circle-react-uikit/Typography";
import { type DateRange, useDateRanges } from "../hooks/useDateRanges";
import { DateRangeDropdown } from "./DateRangeDropdown";
import { useWorkflowUsageModal } from "./WorkflowUsageModal";

interface CreditsUsageSectionProps {
  freeQuantity: number;
  isOverage: boolean;
  progress: number;
  usageCount: number;
}

export const CreditsUsageSection = ({
  freeQuantity,
  isOverage,
  progress,
  usageCount,
}: CreditsUsageSectionProps) => {
  const { currentCommunitySettings } = usePunditUserContext();
  const workflowUsageModal = useWorkflowUsageModal();

  const handleDateRangeSelect = (range: DateRange) => {
    void workflowUsageModal.show({ dateRange: range, freeQuantity });
  };

  const {
    dateRanges,
    handleDateRangeSelect: processDateRangeSelect,
    isLoading,
    fetchBillingCycles,
  } = useDateRanges({
    onDateRangeSelect: handleDateRangeSelect,
  });

  return (
    <div>
      <div className="mb-4">
        <Typography.LabelMd weight="semibold">
          {t("settings.billing.ai_actions.credits")}
        </Typography.LabelMd>
      </div>
      <div className="mb-2">
        <MinimalProgressBar
          progress={progress}
          size="sm"
          type={isOverage ? "danger" : "success"}
        />
      </div>
      <div className="flex justify-between">
        <Typography.LabelSm color="text-light">
          {t("settings.billing.ai_actions.credits_in_tier", {
            current_credits_count: toLocaleString(usageCount),
            max_credits_count_in_tier: toLocaleString(freeQuantity),
          })}
        </Typography.LabelSm>
        <div>
          <Typography.LabelSm color="text-light">
            {!isOverage && t("settings.billing.ai_actions.included")}
          </Typography.LabelSm>
        </div>
      </div>

      {currentCommunitySettings?.ai_workflows_billing_history_modal_enabled && (
        <div className="mt-3 inline-block">
          <DateRangeDropdown
            dateRanges={dateRanges}
            isLoading={isLoading}
            onDropdownClick={fetchBillingCycles}
            onDateRangeSelect={processDateRangeSelect}
          />
        </div>
      )}

      {isOverage && (
        <div className="mt-4 flex flex-col items-start gap-2">
          <Typography.LabelSm color="text-danger">
            {t("settings.billing.ai_actions.overage_message")}
          </Typography.LabelSm>
        </div>
      )}
    </div>
  );
};
